import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  CreditCard,
  Settings,
  Plus,
  Trash2,
  DollarSign,
  Check,
  AlertCircle,
  Key,
  Building2,
  Store,
  Users,
  TrendingUp,
  Clock,
  Receipt
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BillingSettingsPage = () => {
  const { token, user } = useAuth();
  const [activeTab, setActiveTab] = useState('settings');
  const [loading, setLoading] = useState(true);
  
  // Determine user tier
  const isPlatformAdmin = user?.tenant_role === 'platform_admin' || 
                          user?.role_id === 'role_platform_admin' ||
                          user?.role_id === 'role_developer';
  const isBuilderAdmin = user?.tenant_role === 'builder_admin';
  const isSalesCompanyAdmin = user?.tenant_role === 'sales_company_admin';
  
  // Settings state
  const [stripeSettings, setStripeSettings] = useState({
    publishable_key: '',
    secret_key: '',
    webhook_secret: '',
    is_test_mode: true,
    is_configured: false
  });
  
  // Plans state
  const [plans, setPlans] = useState([]);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [planForm, setPlanForm] = useState({
    name: '',
    description: '',
    price: '',
    features: ''
  });
  
  // Transactions state
  const [transactions, setTransactions] = useState([]);
  
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchSettings();
    fetchPlans();
    fetchTransactions();
  }, []);

  const getSettingsEndpoint = () => {
    if (isPlatformAdmin) return `${API_URL}/api/billing/platform/settings`;
    if (isBuilderAdmin) return `${API_URL}/api/billing/builder/${user.builder_id}/settings`;
    if (isSalesCompanyAdmin) return `${API_URL}/api/billing/sales-company/${user.sales_company_id}/settings`;
    return null;
  };

  const getPlansEndpoint = () => {
    if (isPlatformAdmin) return `${API_URL}/api/billing/platform/plans`;
    if (isBuilderAdmin) return `${API_URL}/api/billing/builder/${user.builder_id}/plans`;
    return null;
  };

  const fetchSettings = async () => {
    const endpoint = getSettingsEndpoint();
    if (!endpoint) return;
    
    try {
      const response = await axios.get(endpoint, { headers });
      setStripeSettings({
        ...stripeSettings,
        ...response.data,
        publishable_key: response.data.publishable_key || '',
        secret_key: '',
        webhook_secret: ''
      });
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlans = async () => {
    const endpoint = getPlansEndpoint();
    if (!endpoint) return;
    
    try {
      const response = await axios.get(endpoint, { headers });
      setPlans(response.data);
    } catch (error) {
      console.error('Failed to fetch plans:', error);
    }
  };

  const fetchTransactions = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/billing/transactions`, { headers });
      setTransactions(response.data.transactions || []);
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
    }
  };

  const saveStripeSettings = async () => {
    const endpoint = getSettingsEndpoint();
    if (!endpoint) return;
    
    try {
      await axios.post(endpoint, {
        publishable_key: stripeSettings.publishable_key,
        secret_key: stripeSettings.secret_key,
        webhook_secret: stripeSettings.webhook_secret,
        is_test_mode: stripeSettings.is_test_mode
      }, { headers });
      
      toast.success('Stripe settings saved successfully!');
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    }
  };

  const createPlan = async () => {
    const endpoint = getPlansEndpoint();
    if (!endpoint) return;
    
    try {
      await axios.post(endpoint, {
        name: planForm.name,
        description: planForm.description,
        price: parseFloat(planForm.price),
        features: planForm.features.split('\n').filter(f => f.trim())
      }, { headers });
      
      toast.success('Plan created successfully!');
      setShowPlanModal(false);
      setPlanForm({ name: '', description: '', price: '', features: '' });
      fetchPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create plan');
    }
  };

  const deletePlan = async (planId) => {
    if (!window.confirm('Are you sure you want to delete this plan?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/billing/platform/plans/${planId}`, { headers });
      toast.success('Plan deleted successfully!');
      fetchPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete plan');
    }
  };

  const getTierLabel = () => {
    if (isPlatformAdmin) return 'Platform';
    if (isBuilderAdmin) return 'Builder';
    if (isSalesCompanyAdmin) return 'Sales Company';
    return 'Unknown';
  };

  const getTierDescription = () => {
    if (isPlatformAdmin) return 'Bill Builders and Sales Companies for platform access';
    if (isBuilderAdmin) return 'Bill your Sales Companies for services';
    if (isSalesCompanyAdmin) return 'Bill retail customers for buildings and quotes';
    return '';
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="billing-settings-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Billing Settings
            </h1>
            <p className="text-gray-500 dark:text-gray-400">
              {getTierDescription()}
            </p>
          </div>
          <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
            {getTierLabel()} Tier
          </Badge>
        </div>

        {/* Status Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                  stripeSettings.is_configured 
                    ? 'bg-green-100 dark:bg-green-900/50' 
                    : 'bg-yellow-100 dark:bg-yellow-900/50'
                }`}>
                  {stripeSettings.is_configured ? (
                    <Check className="w-6 h-6 text-green-600 dark:text-green-400" />
                  ) : (
                    <AlertCircle className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
                  )}
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Stripe Status</p>
                  <p className="text-lg font-semibold text-gray-900 dark:text-white">
                    {stripeSettings.is_configured ? 'Connected' : 'Not Configured'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/50 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Active Plans</p>
                  <p className="text-lg font-semibold text-gray-900 dark:text-white">
                    {plans.filter(p => p.is_active).length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/50 rounded-lg flex items-center justify-center">
                  <Receipt className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Transactions</p>
                  <p className="text-lg font-semibold text-gray-900 dark:text-white">
                    {transactions.length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-gray-100 dark:bg-gray-800">
            <TabsTrigger value="settings" className="data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700">
              <Key className="w-4 h-4 mr-2" />
              API Settings
            </TabsTrigger>
            {(isPlatformAdmin || isBuilderAdmin) && (
              <TabsTrigger value="plans" className="data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700">
                <DollarSign className="w-4 h-4 mr-2" />
                Subscription Plans
              </TabsTrigger>
            )}
            <TabsTrigger value="transactions" className="data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700">
              <Receipt className="w-4 h-4 mr-2" />
              Transactions
            </TabsTrigger>
          </TabsList>

          {/* API Settings Tab */}
          <TabsContent value="settings" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  Stripe API Configuration
                </CardTitle>
                <CardDescription>
                  Enter your Stripe API keys to enable payment processing.
                  {stripeSettings.is_test_mode && (
                    <Badge variant="outline" className="ml-2 text-yellow-600 border-yellow-600">
                      Test Mode
                    </Badge>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-gray-700 dark:text-gray-200">Publishable Key</Label>
                  <Input
                    placeholder="pk_test_..."
                    value={stripeSettings.publishable_key}
                    onChange={(e) => setStripeSettings({ ...stripeSettings, publishable_key: e.target.value })}
                    className="mt-1"
                    data-testid="stripe-publishable-key"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Starts with pk_test_ (test) or pk_live_ (production)
                  </p>
                </div>
                
                <div>
                  <Label className="text-gray-700 dark:text-gray-200">Secret Key</Label>
                  <Input
                    type="password"
                    placeholder="sk_test_..."
                    value={stripeSettings.secret_key}
                    onChange={(e) => setStripeSettings({ ...stripeSettings, secret_key: e.target.value })}
                    className="mt-1"
                    data-testid="stripe-secret-key"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Starts with sk_test_ (test) or sk_live_ (production). Never share this!
                  </p>
                </div>
                
                <div>
                  <Label className="text-gray-700 dark:text-gray-200">Webhook Secret (Optional)</Label>
                  <Input
                    type="password"
                    placeholder="whsec_..."
                    value={stripeSettings.webhook_secret}
                    onChange={(e) => setStripeSettings({ ...stripeSettings, webhook_secret: e.target.value })}
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Used to verify webhook events from Stripe
                  </p>
                </div>
                
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="test-mode"
                    checked={stripeSettings.is_test_mode}
                    onChange={(e) => setStripeSettings({ ...stripeSettings, is_test_mode: e.target.checked })}
                    className="rounded"
                  />
                  <Label htmlFor="test-mode" className="text-gray-700 dark:text-gray-200">
                    Test Mode (use test API keys)
                  </Label>
                </div>
                
                <Button onClick={saveStripeSettings} className="mt-4" data-testid="save-stripe-settings">
                  Save Stripe Settings
                </Button>
              </CardContent>
            </Card>

            {/* Help Section */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">How to Get Your Stripe API Keys</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm text-gray-600 dark:text-gray-300">
                <ol className="list-decimal list-inside space-y-2">
                  <li>Go to <a href="https://dashboard.stripe.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">dashboard.stripe.com</a></li>
                  <li>Sign in or create a Stripe account</li>
                  <li>Click on "Developers" in the sidebar</li>
                  <li>Click on "API keys"</li>
                  <li>Copy your Publishable key and Secret key</li>
                  <li>For testing, toggle "Test mode" in the Stripe dashboard</li>
                </ol>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Subscription Plans Tab */}
          {(isPlatformAdmin || isBuilderAdmin) && (
            <TabsContent value="plans" className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Your Subscription Plans ({plans.length})
                </h2>
                <Button onClick={() => setShowPlanModal(true)} data-testid="create-plan-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Plan
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {plans.map((plan) => (
                  <Card key={plan.id} className="relative">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="font-semibold text-gray-900 dark:text-white">
                            {plan.name}
                          </h3>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {plan.description || 'No description'}
                          </p>
                        </div>
                        <Badge variant={plan.is_active ? "default" : "secondary"}>
                          {plan.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                      
                      <div className="mb-4">
                        <span className="text-3xl font-bold text-gray-900 dark:text-white">
                          ${plan.price}
                        </span>
                        <span className="text-gray-500 dark:text-gray-400">/mo</span>
                      </div>
                      
                      {plan.features && plan.features.length > 0 && (
                        <ul className="space-y-2 mb-4">
                          {plan.features.map((feature, i) => (
                            <li key={i} className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                              <Check className="w-4 h-4 text-green-500" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                      )}
                      
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          variant="destructive" 
                          onClick={() => deletePlan(plan.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                
                {plans.length === 0 && (
                  <Card className="col-span-full">
                    <CardContent className="p-12 text-center">
                      <DollarSign className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                        No Plans Yet
                      </h3>
                      <p className="text-gray-500 dark:text-gray-400 mb-4">
                        Create your first subscription plan to start billing
                      </p>
                      <Button onClick={() => setShowPlanModal(true)}>
                        <Plus className="w-4 h-4 mr-2" />
                        Create Plan
                      </Button>
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>
          )}

          {/* Transactions Tab */}
          <TabsContent value="transactions" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Payment History</CardTitle>
              </CardHeader>
              <CardContent>
                {transactions.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 dark:bg-gray-800">
                        <tr>
                          <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                            Date
                          </th>
                          <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                            Description
                          </th>
                          <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                            Payer
                          </th>
                          <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                            Amount
                          </th>
                          <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y dark:divide-gray-700">
                        {transactions.map((tx) => (
                          <tr key={tx.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                            <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                              {new Date(tx.created_at).toLocaleDateString()}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                              {tx.description || 'Payment'}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                              {tx.payer_name}
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                              ${tx.amount.toFixed(2)} {tx.currency?.toUpperCase()}
                            </td>
                            <td className="px-4 py-3">
                              <Badge variant={
                                tx.status === 'paid' ? 'default' :
                                tx.status === 'pending' ? 'secondary' :
                                'destructive'
                              }>
                                {tx.status}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    No transactions yet
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Create Plan Modal */}
      <Dialog open={showPlanModal} onOpenChange={setShowPlanModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Subscription Plan</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-gray-700 dark:text-gray-200">Plan Name *</Label>
              <Input
                required
                placeholder="e.g., Basic, Pro, Enterprise"
                value={planForm.name}
                onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-gray-700 dark:text-gray-200">Description</Label>
              <Input
                placeholder="Brief description of this plan"
                value={planForm.description}
                onChange={(e) => setPlanForm({ ...planForm, description: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-gray-700 dark:text-gray-200">Monthly Price (USD) *</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                required
                placeholder="49.99"
                value={planForm.price}
                onChange={(e) => setPlanForm({ ...planForm, price: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-gray-700 dark:text-gray-200">Features (one per line)</Label>
              <textarea
                className="w-full mt-1 px-3 py-2 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white dark:border-gray-600"
                rows={4}
                placeholder="Feature 1&#10;Feature 2&#10;Feature 3"
                value={planForm.features}
                onChange={(e) => setPlanForm({ ...planForm, features: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPlanModal(false)}>
              Cancel
            </Button>
            <Button onClick={createPlan} disabled={!planForm.name || !planForm.price}>
              Create Plan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default BillingSettingsPage;
